import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import Layout from '../../components/layout/Layout';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from 'react-hot-toast';
import Visibility from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';

const SalesReport = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    totalSales: 0,
    totalInvoices: 0,
    totalReturns: 0,
    netRevenue: 0,
    topProduct: 'N/A',
    topCustomer: 'N/A',
    salesDetails: [],
    returns: [],
    totalOrders: 0
  });
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesReport, setSalesReport] = useState({
    totalOrders: 0,
    totalSales: 0,
    totalReturns: 0,
    netRevenue: 0,
    salesDetails: []
  });
  const [filteredSalesDetails, setFilteredSalesDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Load initial data including last 30 days summary
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [customersRes, productsRes, initialReportRes] = await Promise.all([
          axiosInstance.get(`${API_CONFIG.ENDPOINTS.CUSTOMERS}?size=1000`),
          axiosInstance.get(`${API_CONFIG.ENDPOINTS.PRODUCTS}?size=1000`),
          axiosInstance.get('/reports/sales', {
            params: {
              startDate: startDate,
              endDate: endDate
            }
          })
        ]);
        
        // Set customers data
        const customersData = customersRes.data?.content || [];
        setCustomers(customersData.filter(customer => customer && customer.name));
        
        // Set products data
        const productsData = productsRes.data?.content || [];
        setProducts(productsData.filter(product => product && product.name));
        
        // Set initial report data
        setSalesReport(initialReportRes.data);
        setData({
          ...initialReportRes.data,
          totalOrders: initialReportRes.data.totalOrders || 0,
          totalSales: initialReportRes.data.totalSales || 0,
          totalReturns: initialReportRes.data.totalReturns || 0,
          netRevenue: initialReportRes.data.netRevenue || 0
        });
        setFilteredSalesDetails(initialReportRes.data.salesDetails || []);
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
        if (error.response?.status === 404) {
          toast.error('Unable to connect to the server. Please make sure the backend service is running.');
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to access the sales report. Please contact your administrator.');
        }
        setCustomers([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []); // Empty dependency array as we only want this to run once on mount

  // Add useEffect to handle filtering
  useEffect(() => {
    if (salesReport?.salesDetails) {
      let filtered = [...salesReport.salesDetails];
      
      // Apply customer filter if selected
      if (selectedCustomer) {
        filtered = filtered.filter(sale => 
          sale.customerName.toLowerCase() === selectedCustomer.name.toLowerCase()
        );
      }
      
      setFilteredSalesDetails(filtered);
    } else {
      setFilteredSalesDetails([]);
    }
  }, [salesReport?.salesDetails, selectedCustomer]);

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/reports/sales`, {
        params: {
          startDate: startDate,
          endDate: endDate,
          customerId: selectedCustomer?.id,
          productId: selectedProduct?.id
        }
      });

      // Ensure each sale has an id field
      const salesWithIds = response.data.salesDetails?.map(sale => ({
        ...sale,
        id: sale.id || sale.orderId // Ensure we have an id field
      })) || [];

      setSalesReport({
        ...response.data,
        salesDetails: salesWithIds
      });
      setData({
        ...response.data,
        totalOrders: response.data.totalOrders || 0,
        totalSales: response.data.totalSales || 0,
        totalReturns: response.data.totalReturns || 0,
        netRevenue: response.data.netRevenue || 0
      });
      
      // Set filtered sales details with ensured ids
      let filtered = salesWithIds;
      
      if (selectedCustomer) {
        filtered = filtered.filter(sale => 
          sale.customerName.toLowerCase() === selectedCustomer.name.toLowerCase()
        );
      }
      
      if (selectedProduct) {
        filtered = filtered.filter(sale => 
          sale.items?.some(item => 
            item.productId === selectedProduct.id || 
            item.productName?.toLowerCase() === selectedProduct.name.toLowerCase()
          )
        );
      }
      
      setFilteredSalesDetails(filtered);
      setPage(0);
      
    } catch (error) {
      console.error('Error fetching sales report:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to access the sales report.');
      } else {
        toast.error('Failed to fetch sales report');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update the handleGenerateReport validation
  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.error('End date cannot be earlier than start date');
      return;
    }

    await fetchSalesReport();
  };

  // Modify the handleCustomerChange to not trigger automatic fetch
  const handleCustomerChange = (_, newValue) => {
    setSelectedCustomer(newValue);
    // Remove the automatic fetch
  };

  // Add product change handler (similar to customer change handler)
  const handleProductChange = (_, newValue) => {
    setSelectedProduct(newValue);
    // No automatic fetch, will wait for Generate button click
  };

  const handleViewInvoice = (invoiceId) => {
    window.open(`/invoice/${invoiceId}`, '_blank');
  };

  const handlePrintInvoice = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/sales-orders/${orderId}/receipt`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      // Create a blob from the PDF stream
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `invoice-${orderId}.pdf`;
      
      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice. Please try again.');
    }
  };

  const fetchOrderDetails = async (sale) => {
    if (!sale) {
      toast.error('Invalid order');
      return;
    }
    try {
      // Get the full order details including items
      const response = await axiosInstance.get(`/sales-orders/${sale.id}`);
      if (response.data) {
        console.log('Order details:', response.data);
        setSelectedOrder(response.data);
        setExpandedOrderId(sale.orderNumber);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Failed to show order details');
      setExpandedOrderId(null);
      setSelectedOrder(null);
    }
  };

  const handleViewOrder = (sale) => {
    if (!sale) {
      toast.error('Invalid order');
      return;
    }
    if (expandedOrderId === sale.orderNumber) {
      setExpandedOrderId(null);
      setSelectedOrder(null);
    } else {
      fetchOrderDetails(sale);
    }
  };

  // Add pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const getPaginatedData = () => {
    const startIndex = page * rowsPerPage;
    return filteredSalesDetails.slice(startIndex, startIndex + rowsPerPage);
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      
      // Prepare the data for export using correct property names and fallbacks
      const exportData = filteredSalesDetails.map((sale, index) => ({
        'S.No': index + 1,
        'Order Number': sale.orderNumber || sale.order_number || '-',
        'Customer': sale.customerName || sale.customer?.name || '-',
        'Order Date': sale.orderDate ? format(new Date(sale.orderDate), 'dd/MM/yyyy') : (sale.order_date ? format(new Date(sale.order_date), 'dd/MM/yyyy') : '-'),
        'Invoice Number': sale.invoiceNumber || sale.invoiceNo || sale.invoice_number || '-',
        'Invoice Date': sale.invoiceDate ? format(new Date(sale.invoiceDate), 'dd/MM/yyyy') : (sale.invoice_date ? format(new Date(sale.invoice_date), 'dd/MM/yyyy') : '-'),
        'Amount': sale.finalAmount || sale.amount || sale.total || 0,
        'Status': sale.status || sale.paymentStatus || '-'
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const wscols = [
        { wch: 5 },  // S.No
        { wch: 15 }, // Order Number
        { wch: 30 }, // Customer
        { wch: 12 }, // Order Date
        { wch: 15 }, // Invoice Number
        { wch: 12 }, // Invoice Date
        { wch: 15 }, // Amount
        { wch: 10 }  // Status
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Add company info to the first row
      XLSX.utils.sheet_add_aoa(ws, [
        ['Sales Report'],
        ['Generated on: ' + format(new Date(), 'dd/MM/yyyy HH:mm:ss')],
        ['Period: ' + format(new Date(startDate), 'dd/MM/yyyy') + ' to ' + format(new Date(endDate), 'dd/MM/yyyy')],
        [''] // Empty row for spacing
      ], { origin: 'A1' });

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Sales report exported successfully');
    } catch (error) {
      console.error('Error exporting sales report:', error);
      toast.error('Failed to export sales report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
        <Container maxWidth="xl">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link component={RouterLink} to="/" color="inherit">Dashboard</Link>
            <Link component={RouterLink} to="/reports" color="inherit">Reports</Link>
            <Typography color="text.primary">Sales Report</Typography>
          </Breadcrumbs>

          <Typography variant="h4" gutterBottom>
            Sales Report {!selectedCustomer && !selectedProduct && 'Last 30 Days'}
          </Typography>

          {/* Export Button Row */}
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            <Grid item>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleExportToExcel}
                disabled={isExporting || filteredSalesDetails.length === 0}
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                sx={{ minWidth: 180, height: '48px', fontWeight: 600 }}
              >
                {isExporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </Grid>
          </Grid>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card sx={{ 
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Orders
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : data.totalOrders || '0'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Card sx={{ 
                bgcolor: 'success.light',
                color: 'success.contrastText',
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}>
                <CardContent>
                  <Typography color="inherit" gutterBottom variant="subtitle2">
                    Total Sales
                  </Typography>
                  <Typography color="inherit" variant="h4">
                    {loading ? <CircularProgress size={20} /> : formatCurrency(data.totalSales || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Date Range Selection */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateReport}
                disabled={loading}
                fullWidth
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Generate Report'}
              </Button>
            </Grid>
          </Grid>

          {/* Filters Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={customers || []}
                    getOptionLabel={(option) => option?.name || ''}
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Filter by Customer" 
                        helperText={selectedCustomer ? "Click Generate Report to apply filter" : "Show all customers"}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    noOptionsText="No customers found"
                    loading={loading}
                    loadingText="Loading customers..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={products || []}
                    getOptionLabel={(option) => option?.name || ''}
                    value={selectedProduct}
                    onChange={handleProductChange}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Filter by Product" 
                        helperText={selectedProduct ? "Click Generate Report to apply filter" : "Show all products"}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    noOptionsText="No products found"
                    loading={loading}
                    loadingText="Loading products..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Sales Details Table */}
          <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                  <TableCell>S.No.</TableCell>
                  <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Final Amount</TableCell>
                  <TableCell>Payment Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredSalesDetails.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {selectedCustomer && selectedProduct 
                        ? `No sales records found for customer ${selectedCustomer.name} and product ${selectedProduct.name}`
                        : selectedCustomer
                        ? `No sales records found for customer ${selectedCustomer.name}`
                        : selectedProduct
                        ? `No sales records found for product ${selectedProduct.name}`
                        : 'No sales records found'}
                        </TableCell>
                      </TableRow>
                ) : (
                  getPaginatedData().map((sale, index) => (
                    <React.Fragment key={sale.id || sale.orderId}>
                      <TableRow>
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{sale.orderNumber}</TableCell>
                          <TableCell>{sale.customerName}</TableCell>
                        <TableCell>
                          {format(new Date(sale.orderDate), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sale.amount)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sale.tax)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(sale.finalAmount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sale.paymentStatus}
                            color={
                              sale.paymentStatus === 'PAID'
                                ? 'success'
                                : sale.paymentStatus === 'PENDING'
                                ? 'warning'
                                : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleViewOrder(sale);
                              }}
                              title="View Order Details"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {/* Expanded Order Details Card */}
                        {expandedOrderId === sale.orderNumber && selectedOrder && (
                          <TableRow>
                            <TableCell 
                              colSpan={9} 
                              sx={{ 
                                py: 2,
                                backgroundColor: 'background.default'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  p: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  bgcolor: 'background.paper'
                                }}
                              >
                                <Grid container spacing={3}>
                                  {/* Order Information */}
                                  <Grid item xs={12} md={4}>
                                    <Card elevation={0} sx={{ height: '100%' }}>
                                      <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                          Order Information
                                        </Typography>
                                        <List dense>
                                          <ListItem>
                                            <ListItemText
                                              primary="Order Number"
                                              secondary={selectedOrder.orderNumber}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Order Date"
                                              secondary={format(new Date(selectedOrder.orderDate), 'MMM dd, yyyy')}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Status"
                                              secondary={
                                                <Box component="span" sx={{ display: 'inline-block' }}>
                                                  <Chip
                                                    label={selectedOrder.status}
                                                    color={
                                                      selectedOrder.status === 'DELIVERED' ? 'success' :
                                                      selectedOrder.status === 'PENDING' ? 'warning' : 'default'
                                                    }
                                                    size="small"
                                                  />
                                                </Box>
                                              }
                                            />
                                          </ListItem>
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>

                                  {/* Customer Information */}
                                  <Grid item xs={12} md={4}>
                                    <Card elevation={0} sx={{ height: '100%' }}>
                                      <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                          Customer Information
                                        </Typography>
                                        <List dense>
                                          <ListItem>
                                            <ListItemText
                                              primary="Customer Name"
                                              secondary={selectedOrder.customerName}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Contact"
                                              secondary={selectedOrder.customer?.phone || 'N/A'}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Email"
                                              secondary={selectedOrder.customer?.email || 'N/A'}
                                            />
                                          </ListItem>
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>

                                  {/* Order Summary */}
                                  <Grid item xs={12} md={4}>
                                    <Card elevation={0} sx={{ height: '100%' }}>
                                      <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                          Order Summary
                                        </Typography>
                                        <List dense>
                                          <ListItem>
                                            <ListItemText
                                              primary="Subtotal"
                                              secondary={formatCurrency(selectedOrder.subtotal)}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Tax"
                                              secondary={formatCurrency(selectedOrder.tax)}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary="Discount"
                                              secondary={formatCurrency(selectedOrder.discount || 0)}
                                            />
                                          </ListItem>
                                          <ListItem>
                                            <ListItemText
                                              primary={<Typography variant="subtitle1" fontWeight="bold">Total Amount</Typography>}
                                              secondary={<Typography variant="h6" color="primary">{formatCurrency(selectedOrder.finalAmount)}</Typography>}
                                            />
                                          </ListItem>
                                        </List>
                                      </CardContent>
                                    </Card>
                                  </Grid>

                                  {/* Products Table */}
                                  <Grid item xs={12}>
                                    <Card elevation={0}>
                                      <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                          <Typography variant="h6">
                                            Order Items
                                          </Typography>
                                          <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (selectedOrder?.id) {
                                                handlePrintInvoice(selectedOrder.id);
                                              } else {
                                                toast.error('Order ID not found');
                                              }
                                            }}
                                            startIcon={<PrintIcon />}
                                            size="small"
                                          >
                                            Print Invoice
                                          </Button>
                                        </Box>
                                        <TableContainer>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Product</TableCell>
                                                <TableCell align="right">Quantity</TableCell>
                                                <TableCell align="right">Unit Price</TableCell>
                                                <TableCell align="right">Discount</TableCell>
                                                <TableCell align="right">Tax</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {selectedOrder.items?.map((item) => (
                                                <TableRow key={item.id}>
                                                  <TableCell>{item.product?.name || 'N/A'}</TableCell>
                                                  <TableCell align="right">{item.quantity}</TableCell>
                                                  <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                                                  <TableCell align="right">{formatCurrency(item.discount || 0)}</TableCell>
                                                  <TableCell align="right">{formatCurrency(item.taxAmount || 0)}</TableCell>
                                                  <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                                                </TableRow>
                                              ))}
                                              {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                      <TableRow>
                                                  <TableCell colSpan={6} align="center">
                                                    No items found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
                                  </Grid>
                                </Grid>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            {!loading && filteredSalesDetails.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={filteredSalesDetails.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}
              />
            )}
          </TableContainer>

          {/* Return Details Section */}
          {data.returns && data.returns.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Return Details
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Return ID</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Products</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Handled By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.returns.map((return_) => (
                        <TableRow key={return_.id}>
                          <TableCell>{return_.returnId}</TableCell>
                          <TableCell>{return_.invoiceNumber}</TableCell>
                          <TableCell>{new Date(return_.date).toLocaleDateString()}</TableCell>
                          <TableCell>{return_.products}</TableCell>
                          <TableCell>${return_.amount?.toFixed(2)}</TableCell>
                          <TableCell>{return_.reason}</TableCell>
                          <TableCell>{return_.handledBy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </Layout>
  );
};

export default SalesReport; 